/**
 * Edge detection for the quad-localize path — the replacement for
 * cv2.bilateralFilter + cv2.Canny.
 *
 * FIDELITY NOTE (important): this is the least faithful part of the port.
 *  - cv2.bilateralFilter(9, 60, 60): approximated by a 5x5 Gaussian blur. We
 *    drop the edge-preserving (range) term; for the localizer's purpose
 *    (suppress texture before Canny) a plain blur behaves similarly and any
 *    divergence is absorbed by the downstream confidence gate (quad score
 *    ≥0.30) — a slightly different edge map rarely flips a high-confidence quad,
 *    and when it does the pipeline simply falls back to the foreground path or
 *    the full frame (never loses the document).
 *  - cv2.Canny(40, 120): implemented faithfully (Sobel gradient → non-maximum
 *    suppression → double-threshold hysteresis) with the same low/high
 *    thresholds. The one deliberate simplification is L1 gradient magnitude
 *    (|gx|+|gy|), which is Canny's default (L2gradient=False) in OpenCV — so
 *    this matches OpenCV's default behaviour.
 *
 * Operates on a grayscale Float32Array plane (see image.toGray).
 */
import { Mask } from './image';

/** Separable 5x5 Gaussian blur (σ≈1.4), approximating the bilateral pre-filter. */
export function gaussianBlur(gray: Float32Array, W: number, H: number): Float32Array {
  const k = [1, 4, 6, 4, 1];
  const ksum = 16;
  const tmp = new Float32Array(W * H);
  const out = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let s = 0;
      for (let i = -2; i <= 2; i++) {
        const xx = Math.min(W - 1, Math.max(0, x + i));
        s += gray[y * W + xx] * k[i + 2];
      }
      tmp[y * W + x] = s / ksum;
    }
  }
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let s = 0;
      for (let i = -2; i <= 2; i++) {
        const yy = Math.min(H - 1, Math.max(0, y + i));
        s += tmp[yy * W + x] * k[i + 2];
      }
      out[y * W + x] = s / ksum;
    }
  }
  return out;
}

/**
 * Canny edge detector. Returns a binary {@link Mask} (0/255). Low/high are the
 * Python thresholds (40, 120). Uses L1 gradient (OpenCV default).
 */
export function canny(gray: Float32Array, W: number, H: number, low: number, high: number): Mask {
  const gx = new Float32Array(W * H);
  const gy = new Float32Array(W * H);
  const mag = new Float32Array(W * H);
  // Sobel
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const a = gray[i - W - 1], b = gray[i - W], c = gray[i - W + 1];
      const d = gray[i - 1], f = gray[i + 1];
      const g = gray[i + W - 1], h = gray[i + W], k = gray[i + W + 1];
      const sx = c + 2 * f + k - a - 2 * d - g;
      const sy = g + 2 * h + k - a - 2 * b - c;
      gx[i] = sx;
      gy[i] = sy;
      mag[i] = Math.abs(sx) + Math.abs(sy); // L1, matches Canny L2gradient=False
    }
  }
  // Non-maximum suppression along gradient direction.
  const nms = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const m = mag[i];
      if (m === 0) continue;
      const angle = Math.atan2(gy[i], gx[i]);
      let deg = (angle * 180) / Math.PI;
      if (deg < 0) deg += 180;
      let n1: number, n2: number;
      if (deg < 22.5 || deg >= 157.5) {
        n1 = mag[i - 1]; n2 = mag[i + 1];
      } else if (deg < 67.5) {
        n1 = mag[i - W + 1]; n2 = mag[i + W - 1];
      } else if (deg < 112.5) {
        n1 = mag[i - W]; n2 = mag[i + W];
      } else {
        n1 = mag[i - W - 1]; n2 = mag[i + W + 1];
      }
      nms[i] = m >= n1 && m >= n2 ? m : 0;
    }
  }
  // Double threshold + hysteresis.
  const out = new Uint8Array(W * H); // 0=none, 1=weak, 2=strong
  const stack: number[] = [];
  for (let i = 0; i < W * H; i++) {
    if (nms[i] >= high) { out[i] = 2; stack.push(i); }
    else if (nms[i] >= low) out[i] = 1;
  }
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % W, y = (i / W) | 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
        const ni = ny * W + nx;
        if (out[ni] === 1) { out[ni] = 2; stack.push(ni); }
      }
    }
  }
  const mask: Mask = { data: new Uint8Array(W * H), width: W, height: H };
  for (let i = 0; i < W * H; i++) mask.data[i] = out[i] === 2 ? 255 : 0;
  return mask;
}
